import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  ArrowRight,
  ChevronUp,
  Calendar,
  Clock,
  ArrowLeft,
} from "lucide-react";
import { CareIcon } from "@/components/icons/CareIcon";
import { AnalysisIcon } from "@/components/icons/AnalysisIcon";
import { ResultsIcon } from "@/components/icons/ResultsIcon";
import { CheckmarkIcon } from "@/components/icons/CheckmarkIcon";
import { binIcon } from "@/assets/images";
import editIcon from "@/assets/images/icons/edit.svg";
import { formatPrice } from "@/components/product-step/types";
import { CurrencyAmount } from "@/components/CurrencyAmount";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { marketHasMembership } from "@/config/market";
import { usePrepayEnabled } from "@/hooks/usePrepayEnabled";
import { BookingLanguage, TimeSlot } from "@/types/booking";
import { trackViewCart, trackMriScanToggle } from "@/utils/analytics";
import { useBookingStore } from "@/stores/bookingStore";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import {
  computeBloodMembershipPricing,
  memberContributionCents,
  isMembershipEligible,
  isBloodMembershipBaseProduct,
  isScanPackage,
} from "@/utils/bloodMembershipPricing";
import { useBundleSummary } from "@/hooks/useBundleSummary";
import { CadencePickerModal } from "@/components/booking-form/CadencePickerModal";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { usePaymentStepCta } from "./usePaymentStepCta";
import { PaymentCtaButton } from "./PaymentCtaButton";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import {
  getCadencePriceChf,
  CADENCE_CONTENTS_KEY,
} from "@/utils/membershipPricing";
import { resolveProductKey, resolveClinicName } from "@/locales";
import { languageMap } from "@/i18n";
import { useMembership } from "@/contexts/MembershipContext";

interface Product {
  id: string;
  name: string;
  sort_order?: number;
  mandatory?: boolean;
  stripe_product_id?: string;
  images?: string[];
  components?: string[];
  includes_blood_draw?: boolean;
  blood_values?: Record<
    string,
    | boolean
    | {
        value: boolean;
        sort_order?: number;
        name?: string | Record<string, string>;
      }
  >;
  default_price?: {
    unit_amount: number;
    currency?: string;
  };
  member_price?: {
    id: string;
    unit_amount: number;
    currency: string;
  };
  category?: {
    name: string;
    sort_order: number;
    translations?: Record<string, { name?: string; description?: string }>;
  };
  package_types?: string[] | null;
  product_type?: "standard" | "package" | "addon";
}

interface Clinic {
  id: string;
  name: string;
  address: string;
  address_short?: string | null;
  photo_url?: string | null;
}

interface ExpandableOrderSummaryProps {
  totalPrice: number;
  basePrice?: number; // Add basePrice to show original price
  selectedProducts: Product[];
  selectedAddOns: Product[];
  selectedClinic?: Clinic | null;
  selectedTimeSlots?: TimeSlot[];
  language: BookingLanguage;
  onContinue?: () => void;
  onBack?: () => void;
  onSubmit?: () => void; // New prop for form submission
  currentStep?: string; // New prop to know what step we're on
  isSubmitting?: boolean; // New prop for form submission loading state
  isLoading?: boolean; // New prop for store loading state
  showBackButton?: boolean;
  showContinueButton?: boolean;
  continueDisabled?: boolean;
  backDisabled?: boolean;
  onProductRemove?: (productId: string) => void;
  onAddOnRemove?: (addOnId: string) => void;
  onEditProducts?: () => void;
  onEditAddOns?: () => void;
  onEditLocation?: () => void;
  onEditTimeSlots?: () => void;
  translations: {
    orderSummary: string;
    scan: string;
    addOns: string;
    bloodTests: string;
    location: string;
    timeSlots: string;
    totalPrice: string;
    continue: string;
    back: string;
    remove: string;
    submitRequest?: string; // New translation
  };
}

export const ExpandableOrderSummary = ({
  totalPrice,
  basePrice,
  selectedProducts,
  selectedAddOns,
  selectedClinic,
  selectedTimeSlots = [],
  language,
  onContinue,
  onBack,
  onSubmit,
  currentStep,
  isSubmitting = false,
  isLoading = false,
  showBackButton = false,
  showContinueButton = true,
  continueDisabled = false,
  backDisabled = false,
  onProductRemove,
  onAddOnRemove,
  onEditProducts,
  onEditAddOns,
  onEditLocation,
  onEditTimeSlots,
  translations: t,
}: ExpandableOrderSummaryProps) => {
  const { t: i18n } = useTranslation(undefined, { lng: languageMap[language] });
  const { currency, market } = useMarket();
  const { data: flags } = useFeatureFlags();
  const membershipFeatureEnabled =
    marketHasMembership(market) && (flags?.["membership_v2"] ?? true);
  const prepayEnabled = usePrepayEnabled();
  // Prepay markets (DE/NL) charge the booking up front, so promising "no
  // upfront payment" there is simply false — they keep the free-cancellation
  // half of the promise only.
  const paymentNoticeKey = prepayEnabled
    ? "orderSummary.freeCancellation"
    : "orderSummary.noUpfrontPayment";
  const [isExpanded, setIsExpanded] = useState(false);
  const [cadenceModalOpen, setCadenceModalOpen] = useState(false);

  // Read discount from booking store
  const discount = useBookingStore((state) => state.discount);
  const { isMember, isEntitledProduct, getEffectivePrice } = useMembership();
  const selectedPaymentMode = useBookingFlowStore((s) => s.selectedPaymentMode);
  const offerMembership =
    membershipFeatureEnabled &&
    isMembershipEligible([...selectedProducts, ...selectedAddOns]);
  // isYearlyBlood is the BLOOD-ONLY path-B branch (standalone blood test billed as
  // the yearly membership). A scan cart also offers path B, but routes through
  // isBundleMode (below) instead — so it's excluded here via !cartHasScan. Keeping
  // the two distinct prevents a scan being priced as standalone-blood member
  // pricing while checkout charges the netted bundle + subscription.
  const cartHasScan = selectedProducts.some(isScanPackage);
  const isYearlyBlood =
    !isMember &&
    offerMembership &&
    !cartHasScan &&
    selectedPaymentMode === "yearly";
  // Membership flow (path A): pay on the next page via Stripe, like path B's
  // "pay now". The CTA + payment notice mirror the desktop sidebar.
  const membershipPlanActiveRaw = useMembershipPlanStore((s) => s.active);
  const membershipPlanActive =
    membershipFeatureEnabled && membershipPlanActiveRaw;
  // Bundle view of the cart (is-bundle, cadence price, netted pricing, per-line
  // scan override) — derived once and shared with the desktop sidebar and add-ons
  // step so display matches what useBloodCheckout charges. See useBundleSummary.
  const { isBundleMode, cadenceCents, bundlePricing, scanOverride } =
    useBundleSummary(selectedProducts, selectedAddOns);

  // Detach the membership from the cart without dropping the scan (scan reverts to
  // its normal non-member price). Store action so the removal persists to the
  // session — see bookingFlowStore.detachMembership. Wired to the remove control
  // on the recurring line and the synthesized membership line.
  const detachMembership = useBookingFlowStore((s) => s.detachMembership);
  // Drops the scan and keeps the membership — see the desktop sidebar for why
  // the two swap roles on the membership-first path.
  const detachScanFromMembership = useBookingFlowStore(
    (s) => s.detachScanFromMembership,
  );
  const membershipFirst = useMembershipPlanStore((s) => s.membershipFirst);
  // The details-step CTA — one shared decision, rendered via <PaymentCtaButton>.
  const paymentCta = usePaymentStepCta(language);

  // Membership flow: replace the baseline blood test with a synthesized "Ahead
  // membership" line priced at the chosen cadence, mirroring the desktop sidebar
  // so the mobile summary shows the same line, groups and member totals.
  const membershipPlan = useMembershipPlanStore((s) => s.plan);
  const membershipTiers = useMembershipTiers().tiers;
  const membershipProduct: Product = {
    id: "membership-plan",
    name: "membershipCadence.orderLineName",
    images: [],
    default_price: {
      unit_amount: cadenceCents,
      currency: "chf",
    },
    product_type: "package",
  };
  // Mirror the desktop sidebar: show the "Ahead membership" line for the
  // membership flow (path A) and for the blood-test upsell (path B) once the
  // user picks "become a member" — the standalone blood test becomes the
  // membership line, add-ons stay as one-time items.
  const hasStandaloneBlood = selectedProducts.some(
    isBloodMembershipBaseProduct,
  );
  const showMembership =
    membershipFeatureEnabled &&
    (membershipPlanActive ||
      (isYearlyBlood && currentStep === "details" && hasStandaloneBlood));
  // Bundle mode: keep scan packages in effectiveProducts (don't replace with the
  // synthesized membership line — the membership is shown in a separate recurring
  // section). Standalone blood membership still replaces products as before.
  const effectiveProducts =
    showMembership && !isBundleMode ? [membershipProduct] : selectedProducts;
  const effectiveAddOns = selectedAddOns;

  // Blood membership experiment: non-member who chose "pay now" gets member
  // pricing; the membership flow always shows member pricing.
  const bloodPricing = computeBloodMembershipPricing([
    ...effectiveProducts,
    ...effectiveAddOns,
  ]);
  const showMemberPricing =
    membershipFeatureEnabled &&
    (showMembership ||
      isBundleMode ||
      (!isMember &&
        isYearlyBlood &&
        currentStep === "details" &&
        bloodPricing.savingsCents > 0));
  // Pre-voucher base (member rate when membership pricing applies, else the
  // full total the parent passed). The voucher is subtracted on top, franc-
  // rounded so subtotal − discount = total exactly.
  const summaryBase = showMemberPricing
    ? bloodPricing.memberTotalCents
    : basePrice || totalPrice; // pre-voucher base; the parent's totalPrice is already discounted, so re-subtracting it here double-counted the voucher (→ 0€)
  // The voucher applies to what is PAID NOW — in bundle mode the bundle pay-now
  // total (netted scan + add-ons + first cadence invoice). The old bundle
  // branch never subtracted the voucher (same defect as the desktop sidebar).
  const summaryPreDiscount =
    isBundleMode && bundlePricing ? bundlePricing.payNowCents : summaryBase;
  // Membership carts pass the individual invoice lines (bundle lines, or the
  // blood model's member lines): Stripe rounds a percentage coupon per line,
  // so a total computed on the sum is not the total charged. The pay-later
  // path keeps the aggregate — that quote is recorded, not invoiced per line.
  const summaryVoucherCents = voucherDiscountCents(
    discount,
    isBundleMode && bundlePricing
      ? bundlePricing.lines
      : showMemberPricing
        ? bloodPricing.memberLines
        : summaryPreDiscount,
  );
  const displayTotalPrice = summaryPreDiscount - summaryVoucherCents;

  // The whole membership / blood-cadence flow collapses to legacy behavior for
  // DE / flag-off via `membershipFeatureEnabled`. This single boolean gates the
  // checkout CTA and the payment-notice rows.
  const membershipFlowActive =
    membershipFeatureEnabled && (membershipPlanActive || isYearlyBlood);

  // Detect keyboard open state to hide the bar on the details step.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      setKeyboardOpen(vv.height < window.innerHeight - 150);
    };
    vv.addEventListener("resize", update);
    return () => vv.removeEventListener("resize", update);
  }, []);

  const toggleExpansion = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);

    // Track view_cart event when expanding the order summary
    if (newExpandedState) {
      const cartItems = [
        ...selectedProducts.map((product) => ({
          item_id: product.id,
          item_name: product.name,
          item_category: "scan",
          price: (product.default_price?.unit_amount || 0) / 100, // cents → major unit
          currency,
          quantity: 1,
        })),
        ...selectedAddOns.map((addOn) => ({
          item_id: addOn.id,
          item_name: addOn.name,
          item_category: "add-on",
          price: (addOn.default_price?.unit_amount || 0) / 100, // cents → major unit
          currency,
          quantity: 1,
        })),
      ];

      trackViewCart(cartItems, totalPrice / 100, currency); // cents → major unit
    }
  };

  // Which control a cart line carries, and what it does. Shared by BOTH render
  // sites of this component and by the section headers: one-line sections
  // hoist their control up next to the label (the Recurring card set the
  // pattern), add-on sections keep per-line bins — several lines, several
  // targets. The synthesized membership line isn't a real product — removing
  // it must detach the membership (store flag), not filter the product list.
  // Membership-first: the scan was added to a booking that started from the
  // membership, so it gets a bin that drops just the scan.
  const productLineControls = (
    product: Product,
  ): { onEdit?: () => void; onRemove?: (id: string) => void } => {
    const isMandatory =
      product.id === "prod_RMmfeLnfXxMKdO" || product.mandatory === true;
    const isPackage = product.product_type === "package";
    const isProductAddOn = selectedAddOns.includes(product);
    const isBundledScanLine =
      membershipFirst && isBundleMode && isPackage && !isProductAddOn;
    const removeHandler = isProductAddOn
      ? onAddOnRemove
      : product.id === "membership-plan"
        ? () => detachMembership()
        : isBundledScanLine
          ? () => {
              trackMriScanToggle(
                false,
                product.name,
                product.default_price?.unit_amount ?? null,
                "cart",
              );
              detachScanFromMembership();
            }
          : onProductRemove;
    const shouldShowEditIcon =
      (isPackage || isMandatory) &&
      !isProductAddOn &&
      currentStep !== "product" &&
      !isBundledScanLine;
    const shouldShowRemoveIcon =
      (!isPackage && !isMandatory) || isProductAddOn || isBundledScanLine;
    return {
      onEdit: shouldShowEditIcon ? onEditProducts : undefined,
      onRemove: shouldShowRemoveIcon ? removeHandler : undefined,
    };
  };

  // The pencil-or-bin a one-line section shows in its header row.
  const sectionHeaderControl = (product: Product) => {
    const { onEdit, onRemove } = productLineControls(product);
    if (onEdit)
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
        >
          <img src={editIcon} alt="Edit" className="h-4 w-4" />
        </button>
      );
    if (onRemove)
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(product.id);
          }}
          className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 transition-colors rounded-md hover:bg-gray-200"
          aria-label={t.remove}
        >
          <img src={binIcon} alt={t.remove} className="h-3.5 w-3.5" />
        </button>
      );
    return null;
  };

  const renderProductItem = (
    product: Product,
    onEdit?: () => void,
    onRemove?: (id: string) => void,
    overrideDiscountedCents?: number,
  ) => {
    const originalAmount = product.default_price?.unit_amount || 0;
    const isEntitled = isEntitledProduct(product.stripe_product_id);
    const discountedAmount =
      overrideDiscountedCents !== undefined
        ? overrideDiscountedCents
        : isMember
          ? getEffectivePrice(
              product as Parameters<typeof getEffectivePrice>[0],
            )
          : showMemberPricing
            ? memberContributionCents(product)
            : originalAmount;
    const hasDiscount =
      (isMember || showMemberPricing) && discountedAmount !== originalAmount;
    const savings = originalAmount - discountedAmount;

    return (
      <div key={product.id} className="contents">
        <div className="flex items-center gap-3 py-1">
          <div
            className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
            style={{ boxShadow: "0px 4px 20px 0px rgba(0,0,0,0.08)" }}
          >
            {product.images?.[0] ? (
              <img
                src={product.images[0]}
                alt={resolveProductKey(language, product.name)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 bg-gray-400 rounded opacity-60"></div>
            )}
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-1 pb-0.5">
            <p
              className="font-medium text-neutral-900 truncate"
              style={{ fontSize: 14, lineHeight: "20px" }}
            >
              {resolveProductKey(language, product.name)}
            </p>
            {hasDiscount ? (
              <p className="text-sm text-neutral-900">
                <CurrencyAmount market={market}>
                  <span className="line-through text-gray-400 font-normal">
                    {formatPrice(originalAmount, market, true)}
                  </span>{" "}
                  <span className="font-bold">
                    {formatPrice(discountedAmount, market, true)}
                  </span>
                </CurrencyAmount>
              </p>
            ) : (
              <p className="text-sm text-neutral-900">
                {formatPrice(originalAmount, market)}
              </p>
            )}
          </div>
          {(onEdit || onRemove) && (
            <div className="flex-shrink-0">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-200"
                  aria-label="Edit product selection"
                >
                  <img src={editIcon} alt="Edit" className="h-4 w-4" />
                </button>
              )}
              {onRemove && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(product.id);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 transition-colors rounded-md hover:bg-gray-200"
                  aria-label="Remove item"
                >
                  <img src={binIcon} alt="Remove" className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        {/* Membership line: cadence contents on its own full-width row so the
            label doesn't wrap beside the price. */}
        {product.id === "membership-plan" && (
          <p className="text-xs text-neutral-500 pl-[60px] pb-1">
            {i18n(CADENCE_CONTENTS_KEY[membershipPlan])}
          </p>
        )}
        {hasDiscount && savings > 0 && isEntitled && (
          <div className="flex items-center justify-end px-1 pb-1">
            <span className="text-sm text-deep-green-600">
              {i18n("membership.included")}
            </span>
          </div>
        )}
      </div>
    );
  };

  // `editInHeader` — the caller's section header already carries the pencil,
  // so the clinic line must not repeat it. The second render site has no
  // header row at all and keeps the line pencil.
  const renderClinicInfo = (editInHeader = false) => {
    if (!selectedClinic) return null;

    return (
      // pb-2, not py-2 — see the same note in PersistentDesktopSidebar: the
      // section header's mb-2 already provides the gap above the photo.
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
            {selectedClinic.photo_url ? (
              <img
                src={selectedClinic.photo_url}
                alt={resolveClinicName(language, selectedClinic)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <div className="w-6 h-6 bg-gray-400 rounded"></div>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900">
              {resolveClinicName(language, selectedClinic)}
            </p>
            <p className="text-sm text-gray-600">
              {selectedClinic.address_short || selectedClinic.address}
            </p>
          </div>
        </div>
        {!editInHeader && onEditLocation && currentStep !== "clinic" && (
          <div className="flex-shrink-0 ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditLocation();
              }}
              className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
              aria-label="Edit location"
            >
              <img src={editIcon} alt="Edit" className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // `editInHeader` — the caller's section header carries the single pencil
  // (every slot edits the same date step, so per-slot pencils were
  // duplicates). The headerless render site keeps the per-slot pencils.
  const renderTimeSlots = (editInHeader = false) => {
    if (!selectedTimeSlots || selectedTimeSlots.length === 0) return null;

    const formatDate = (date: Date | string) => {
      // Defensive: ensure date is a Date object
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return "Invalid date";

      const locale = language;
      return dateObj.toLocaleDateString(locale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    return (
      <div className="space-y-3">
        {selectedTimeSlots.map((slot, index) => {
          const slotLabel =
            index === 0
              ? i18n("orderSummary.preferred")
              : i18n("orderSummary.alternative");

          // Defensive: ensure date is a Date object for key generation
          const slotDate =
            slot.date instanceof Date ? slot.date : new Date(slot.date);
          const dateKey = !isNaN(slotDate.getTime())
            ? slotDate.toDateString()
            : `invalid-${index}`;

          return (
            <div key={`${dateKey}-${slot.time}`}>
              <p className="text-xs text-gray-500 mb-1">{slotLabel}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">
                      {formatDate(slot.date)}
                    </p>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{slot.time}</span>
                    </div>
                  </div>
                </div>
                {!editInHeader &&
                  onEditTimeSlots &&
                  currentStep !== "date" &&
                  currentStep !== "time" && (
                    <div className="flex-shrink-0 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditTimeSlots();
                        }}
                        className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
                        aria-label="Edit time slots"
                      >
                        <img src={editIcon} alt="Edit" className="h-4 w-4" />
                      </button>
                    </div>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Desktop Sidebar Component
  const DesktopSidebar = () => (
    <div className="fixed top-0 right-0 h-screen w-1/4 bg-white border-l border-gray-200 shadow-xl flex flex-col z-50">
      {/* Header with title */}
      <div className="p-6">
        <h2 className={`text-xl text-gray-900 font-medium`}>
          {i18n("orderSummary.bookingSummary")}
        </h2>
        {selectedProducts.length === 0 && selectedAddOns.length === 0 && (
          <p className="text-sm text-gray-700 italic mt-2">
            {i18n("orderSummary.notSelectedYet")}
          </p>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        {/* flex + gap, not space-y: the membership line reorders to the top on
            the membership-first path and space-y lays margins out in DOM
            order, not visual order. */}
        <div className="flex flex-col gap-4">
          {(() => {
            // Combine all products from both sources
            const allProducts = [...effectiveProducts, ...effectiveAddOns];

            // If no products are selected, show USP box
            if (allProducts.length === 0) {
              return (
                <div className="border-2 border-gray-200 rounded-2xl p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-gray-500">
                      <CareIcon />
                      <p className="text-sm">
                        {i18n("orderSummary.careHighLevel")}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500">
                      <AnalysisIcon />
                      <p className="text-sm text-gray-500">
                        {i18n("orderSummary.inDepthAnalysis")}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500">
                      <ResultsIcon />
                      <p className="text-sm text-gray-500">
                        {i18n("orderSummary.resultsInDays")}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }

            // Group products by category name, with special handling for add-ons
            const productsByCategory = new Map<
              string,
              { products: Product[]; isAddOn: boolean }
            >();

            allProducts.forEach((product) => {
              let categoryName: string;
              let isAddOn = false;

              // Check if this product is an add-on (from selectedAddOns array)
              if (selectedAddOns.includes(product)) {
                categoryName = t.addOns; // Use "Add-ons" for items from selectedAddOns
                isAddOn = true;
              } else if (product.category?.name) {
                // Use resolveProductKey to handle translation keys (e.g., "category.bloodTests")
                categoryName = resolveProductKey(
                  language,
                  product.category.name,
                );
              } else {
                // Fallback: categorize by blood test detection or known package names
                const isBloodTest =
                  product.name.toLowerCase().includes("blood") ||
                  (product.blood_values &&
                    Object.keys(product.blood_values).length > 0);
                const isKnownPackage =
                  product.name.toLowerCase().includes("ahead core") ||
                  product.name.toLowerCase().includes("package") ||
                  product.product_type === "package";

                if (isKnownPackage) {
                  categoryName = i18n("orderSummary.packages");
                } else {
                  categoryName = isBloodTest ? t.bloodTests : t.scan;
                }
              }

              // Membership flow (standalone blood): relabel groups by billing cadence.
              // Bundle mode keeps natural category names.
              if (showMembership && !isBundleMode) {
                categoryName = isAddOn
                  ? i18n("orderSummary.oneTimeAddOns")
                  : i18n("orderSummary.recurringAnnually");
              }

              if (!productsByCategory.has(categoryName)) {
                productsByCategory.set(categoryName, { products: [], isAddOn });
              }
              productsByCategory.get(categoryName)!.products.push(product);
            });

            const sortProducts = (products: Product[]) => {
              return products.sort((a, b) => {
                // Mandatory products always go first (same logic as build-your-own step)
                const aIsMandatory =
                  a.id === "prod_RMmfeLnfXxMKdO" || a.mandatory === true;
                const bIsMandatory =
                  b.id === "prod_RMmfeLnfXxMKdO" || b.mandatory === true;

                if (aIsMandatory && !bIsMandatory) return -1;
                if (bIsMandatory && !aIsMandatory) return 1;

                // Both are not mandatory, sort by sort_order if available
                if (a.sort_order != null && b.sort_order != null) {
                  return a.sort_order - b.sort_order;
                }

                // Fallback to alphabetical sorting
                return a.name.localeCompare(b.name);
              });
            };

            // Convert map to array and sort categories by their database sort_order (same as build-your-own step)
            const categoriesArray = Array.from(productsByCategory.entries());
            const sortedCategories = categoriesArray.sort(
              (
                [categoryA, { products: productsA, isAddOn: isAddOnA }],
                [categoryB, { products: productsB, isAddOn: isAddOnB }],
              ) => {
                // Add-ons always come last (they don't have a database category)
                if (isAddOnA && !isAddOnB) return 1;
                if (!isAddOnA && isAddOnB) return -1;
                if (isAddOnA && isAddOnB) return 0; // Both are add-ons, maintain order

                // For non-add-on categories, use the sort_order from the first product's category
                const categoryAOrder =
                  productsA[0]?.category?.sort_order ?? 999;
                const categoryBOrder =
                  productsB[0]?.category?.sort_order ?? 999;

                if (categoryAOrder !== categoryBOrder) {
                  return categoryAOrder - categoryBOrder;
                }

                // Fallback to alphabetical sorting if sort_order is the same
                return categoryA.localeCompare(categoryB);
              },
            );

            return (
              <>
                {sortedCategories.map(
                  ([categoryName, { products, isAddOn }]) => (
                    <div
                      key={categoryName}
                      className="bg-gray-100 rounded-2xl px-5 py-3"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">
                          {categoryName}
                        </h4>
                        {/* Single edit button for add-ons category - hide when on addons step */}
                        {isAddOn &&
                          onEditAddOns &&
                          currentStep !== "addons" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditAddOns();
                              }}
                              className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-200"
                              aria-label="Edit add-ons"
                            >
                              <img
                                src={editIcon}
                                alt="Edit"
                                className="h-4 w-4"
                              />
                            </button>
                          )}
                        {!isAddOn &&
                          products.length === 1 &&
                          sectionHeaderControl(products[0])}
                      </div>
                      <div className="space-y-1">
                        {sortProducts(products).map((product) => {
                          const { onEdit, onRemove } =
                            productLineControls(product);
                          // A one-line section already showed this control in
                          // its header row — don't repeat it on the line.
                          const inHeader = !isAddOn && products.length === 1;
                          // Bundle mode: scan package rendered at netted member price.
                          return renderProductItem(
                            product,
                            inHeader ? undefined : onEdit,
                            inHeader ? undefined : onRemove,
                            scanOverride(
                              product,
                              selectedAddOns.includes(product),
                            ),
                          );
                        })}
                      </div>
                    </div>
                  ),
                )}
                {/* Bundle: the recurring membership line. Membership-first lifts it
                    above the one-time items and gives it the pencil back to the
                    start; the scan below then carries the bin. */}
                {isBundleMode && bundlePricing && (
                  <div
                    className={`bg-gray-100 rounded-2xl px-5 py-3 ${
                      membershipFirst ? "order-first" : ""
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">
                        {i18n("orderSummary.recurringHeading")}
                      </h4>
                      {membershipFirst ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditProducts?.();
                          }}
                          className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
                        >
                          <img src={editIcon} alt="Edit" className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            detachMembership();
                          }}
                          className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 transition-colors rounded-md hover:bg-gray-200"
                          aria-label={i18n("membershipUpsell.remove")}
                        >
                          <img
                            src={binIcon}
                            alt={i18n("membershipUpsell.remove")}
                            className="h-3.5 w-3.5"
                          />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <p
                        className="font-medium text-neutral-900 truncate"
                        style={{ fontSize: 14, lineHeight: "20px" }}
                      >
                        {i18n("membershipCadence.orderLineName")}
                      </p>
                      <p className="text-sm text-neutral-900 flex-shrink-0 pl-4">
                        {formatPrice(cadenceCents, market)}/yr
                      </p>
                    </div>
                    <p className="text-xs text-neutral-500 pt-0.5">
                      {i18n("orderSummary.membershipChargedToday")}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCadenceModalOpen(true);
                      }}
                      className="mt-1 text-xs font-normal normal-case text-neutral-500 underline"
                    >
                      {i18n("paymentInfo.changeCadence")}
                    </button>
                  </div>
                )}
              </>
            );
          })()}

          {/* Location Section. Pencil in the header row, clinic line clean. */}
          {selectedClinic && (
            <div className="bg-gray-100 rounded-2xl px-5 py-3">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  {t.location}
                </h4>
                {onEditLocation && currentStep !== "clinic" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditLocation();
                    }}
                    className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
                    aria-label="Edit location"
                  >
                    <img src={editIcon} alt="Edit" className="h-4 w-4" />
                  </button>
                )}
              </div>
              {renderClinicInfo(true)}
            </div>
          )}

          {/* Time Slots Section. One pencil in the header row. */}
          {selectedTimeSlots && selectedTimeSlots.length > 0 && (
            <div className="bg-gray-100 rounded-2xl px-5 py-3">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  {t.timeSlots}
                </h4>
                {onEditTimeSlots &&
                  currentStep !== "date" &&
                  currentStep !== "time" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditTimeSlots();
                      }}
                      className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
                      aria-label="Edit time slots"
                    >
                      <img src={editIcon} alt="Edit" className="h-4 w-4" />
                    </button>
                  )}
              </div>
              {renderTimeSlots(true)}
            </div>
          )}
        </div>
      </div>

      {/* Footer with total price, guarantee, and navigation */}
      <div className="p-6 border-t border-gray-200">
        {/* Payment notice — hidden in the membership flow (parity with sidebar) */}
        {!membershipFlowActive && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-4">
            <CheckmarkIcon />
            <span>
              {showMemberPricing
                ? i18n("orderSummary.yearlySubscription")
                : i18n(paymentNoticeKey)}
            </span>
          </div>
        )}
        <div className="mb-4">
          {/* Membership savings breakdown — suppressed in bundle mode (netting is shown inline) */}
          {!isBundleMode &&
            (isMember || showMemberPricing) &&
            (() => {
              const allItems = [...selectedProducts, ...selectedAddOns];
              const originalTotal = allItems.reduce(
                (sum, p) => sum + (p.default_price?.unit_amount || 0),
                0,
              );
              const effectiveBase = showMemberPricing
                ? bloodPricing.memberTotalCents
                : basePrice || totalPrice;
              // basePrice is whole-CHF (floored in getEffectivePrice) so arithmetic is exact
              const savings = originalTotal - effectiveBase;
              if (savings <= 0) return null;
              return (
                <div className="mb-3 space-y-1.5">
                  <div className="flex items-center justify-between text-sm text-green-600">
                    <span>{i18n("membership.memberDiscount")}</span>
                    <span key={savings}>-{formatPrice(savings, market)}</span>
                  </div>
                  <div className="border-t border-gray-200 my-2" />
                </div>
              );
            })()}
          {/* Show coupon discount breakdown. Bundle carts included: excluding
              them dated from when the bundle total never subtracted the
              voucher, so hiding the row kept the numbers "consistent" by
              omission — the total nets the voucher now, and a discount the
              customer can't see is worse than one they can audit. basePrice
              still gates the non-bundle case (it's the row's subtotal base);
              a bundle derives its base from bundlePricing instead. */}
          {discount &&
            discount.value > 0 &&
            (isBundleMode ? bundlePricing : basePrice) && (
              <div className="mb-3 space-y-1">
                {!isMember && (
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>{i18n("orderSummary.subtotal")}</span>
                    <span>{formatPrice(summaryPreDiscount, market)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm text-green-600">
                  <span>
                    {i18n("orderSummary.invitationCode")} ({discount.code})
                  </span>
                  <span data-testid="voucher-discount-amount">
                    -{formatPrice(summaryVoucherCents, market)}
                  </span>
                </div>
                {/* On a recurring membership, say whether the voucher is a one-off
                  or applies to every renewal. */}
                {showMembership && discount.duration && (
                  <div className="text-xs text-gray-500">
                    {discount.duration === "once"
                      ? i18n("orderSummary.discountRenewsAt", {
                          price: getCadencePriceChf(
                            membershipPlan,
                            membershipTiers,
                          ),
                          defaultValue: "Renews at CHF {{price}}/year",
                        })
                      : i18n("orderSummary.discountEveryRenewal")}
                  </div>
                )}
                <div className="border-t border-gray-200 my-2"></div>
              </div>
            )}
          <div
            key={totalPrice}
            className="flex items-center justify-between"
            style={{ contain: "paint" }}
          >
            <span className="text-lg font-medium text-gray-900">
              {t.totalPrice}
            </span>
            <span className="flex items-baseline gap-2 whitespace-nowrap">
              {!isBundleMode &&
                showMemberPricing &&
                totalPrice > displayTotalPrice && (
                  <span className="text-sm text-gray-400 line-through">
                    {formatPrice(totalPrice, market)}
                  </span>
                )}
              <span
                data-testid="order-summary-total"
                className="text-xl font-bold text-gray-900"
              >
                {formatPrice(displayTotalPrice, market)}
              </span>
            </span>
          </div>
          {/* Bundle: recurring annotation below total */}
          {isBundleMode && bundlePricing && (
            <p className="text-xs text-neutral-500 text-right mt-1">
              {i18n("orderSummary.recurringHeading")}:{" "}
              {formatPrice(bundlePricing.recurringCents, market)}/yr
            </p>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex gap-4">
          {showBackButton && onBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={backDisabled}
              className="bg-neutral-50 hover:bg-neutral-100 text-aubergine-800 rounded-full h-[44px] px-8 font-medium text-base normal-case flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" />
              {t.back}
            </button>
          )}

          {/* Show submit button on details step, continue button otherwise */}
          {currentStep === "details" ? (
            <PaymentCtaButton
              cta={paymentCta}
              className={`${showBackButton ? "flex-1" : "w-full"} px-8`}
              submitTestId="order-summary-panel-submit"
              formId="booking-form"
            />
          ) : (
            showContinueButton && (
              <button
                type="button"
                onClick={onContinue}
                disabled={continueDisabled || !onContinue}
                className={`${showBackButton ? "flex-1" : "w-full"} bg-aubergine-800 hover:bg-aubergine-800/90 text-white rounded-full h-[44px] px-8 font-medium text-base normal-case flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none`}
              >
                {t.continue}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile/Tablet View (< xl screens) - Bottom expandable, hidden until a product is selected */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 xl:hidden transition-transform duration-300"
        style={{
          transform:
            selectedProducts.length === 0
              ? "translateY(100%)"
              : keyboardOpen && currentStep === "details"
                ? "translateY(100%)"
                : undefined,
        }}
      >
        <div className="max-w-[1280px] mx-auto">
          {/* Expandable Content */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out border-b border-gray-100 ${
              isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="px-4 py-4 overflow-y-auto max-h-[460px]">
              <div className="flex flex-col gap-4 pb-4">
                {/* Group products by category and source, just like the build-your-own step */}
                {(() => {
                  // Combine all products from both sources
                  const allProducts = [
                    ...effectiveProducts,
                    ...effectiveAddOns,
                  ];

                  // Group products by category name, with special handling for add-ons
                  const productsByCategory = new Map<
                    string,
                    { products: Product[]; isAddOn: boolean }
                  >();

                  allProducts.forEach((product) => {
                    let categoryName: string;
                    let isAddOn = false;

                    // Check if this product is an add-on (from selectedAddOns array)
                    if (selectedAddOns.includes(product)) {
                      categoryName = t.addOns; // Use "Add-ons" for items from selectedAddOns
                      isAddOn = true;
                    } else if (product.category?.name) {
                      // Use resolveProductKey to handle translation keys (e.g., "category.bloodTests")
                      categoryName = resolveProductKey(
                        language,
                        product.category.name,
                      );
                    } else {
                      // Fallback: categorize by blood test detection or known package names
                      const isBloodTest =
                        product.name.toLowerCase().includes("blood") ||
                        (product.blood_values &&
                          Object.keys(product.blood_values).length > 0);
                      const isKnownPackage =
                        product.name.toLowerCase().includes("ahead core") ||
                        product.name.toLowerCase().includes("package") ||
                        product.product_type === "package";

                      if (isKnownPackage) {
                        categoryName = i18n("orderSummary.packages");
                      } else {
                        categoryName = isBloodTest ? t.bloodTests : t.scan;
                      }
                    }

                    // Membership flow (standalone blood): relabel groups by billing cadence.
                    // Bundle mode keeps natural category names.
                    if (showMembership && !isBundleMode) {
                      categoryName = isAddOn
                        ? i18n("orderSummary.oneTimeAddOns")
                        : i18n("orderSummary.recurringAnnually");
                    }

                    if (!productsByCategory.has(categoryName)) {
                      productsByCategory.set(categoryName, {
                        products: [],
                        isAddOn,
                      });
                    }
                    productsByCategory
                      .get(categoryName)!
                      .products.push(product);
                  });

                  const sortProducts = (products: Product[]) => {
                    return products.sort((a, b) => {
                      // Mandatory products always go first (same logic as build-your-own step)
                      const aIsMandatory =
                        a.id === "prod_RMmfeLnfXxMKdO" || a.mandatory === true;
                      const bIsMandatory =
                        b.id === "prod_RMmfeLnfXxMKdO" || b.mandatory === true;

                      if (aIsMandatory && !bIsMandatory) return -1;
                      if (bIsMandatory && !aIsMandatory) return 1;

                      // Both are not mandatory, sort by sort_order if available
                      if (a.sort_order != null && b.sort_order != null) {
                        return a.sort_order - b.sort_order;
                      }

                      // Fallback to alphabetical sorting
                      return a.name.localeCompare(b.name);
                    });
                  };

                  // Convert map to array and sort categories by their database sort_order (same as build-your-own step)
                  const categoriesArray = Array.from(
                    productsByCategory.entries(),
                  );
                  const sortedCategories = categoriesArray.sort(
                    (
                      [categoryA, { products: productsA, isAddOn: isAddOnA }],
                      [categoryB, { products: productsB, isAddOn: isAddOnB }],
                    ) => {
                      // Add-ons always come last (they don't have a database category)
                      if (isAddOnA && !isAddOnB) return 1;
                      if (!isAddOnA && isAddOnB) return -1;
                      if (isAddOnA && isAddOnB) return 0; // Both are add-ons, maintain order

                      // For non-add-on categories, use the sort_order from the first product's category
                      const categoryAOrder =
                        productsA[0]?.category?.sort_order ?? 999;
                      const categoryBOrder =
                        productsB[0]?.category?.sort_order ?? 999;

                      if (categoryAOrder !== categoryBOrder) {
                        return categoryAOrder - categoryBOrder;
                      }

                      // Fallback to alphabetical sorting if sort_order is the same
                      return categoryA.localeCompare(categoryB);
                    },
                  );

                  return (
                    <>
                      {sortedCategories.map(
                        ([categoryName, { products, isAddOn }]) => (
                          <div
                            key={categoryName}
                            className="bg-gray-100 rounded-2xl px-5 py-3"
                          >
                            <div className="mb-2 flex items-center justify-between">
                              <h4 className="text-sm font-medium text-gray-900">
                                {categoryName}
                              </h4>
                              {/* Single edit button for add-ons category - hide when on addons step */}
                              {isAddOn &&
                                onEditAddOns &&
                                currentStep !== "addons" && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEditAddOns();
                                    }}
                                    className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-200"
                                    aria-label="Edit add-ons"
                                  >
                                    <img
                                      src={editIcon}
                                      alt="Edit"
                                      className="h-4 w-4"
                                    />
                                  </button>
                                )}
                              {!isAddOn &&
                                products.length === 1 &&
                                sectionHeaderControl(products[0])}
                            </div>
                            <div className="space-y-1">
                              {sortProducts(products).map((product) => {
                                const { onEdit, onRemove } =
                                  productLineControls(product);
                                // A one-line section already showed this
                                // control in its header row.
                                const inHeader =
                                  !isAddOn && products.length === 1;
                                // Bundle mode: scan package rendered at netted member price.
                                return renderProductItem(
                                  product,
                                  inHeader ? undefined : onEdit,
                                  inHeader ? undefined : onRemove,
                                  scanOverride(
                                    product,
                                    selectedAddOns.includes(product),
                                  ),
                                );
                              })}
                            </div>
                          </div>
                        ),
                      )}
                      {/* Bundle: the recurring membership line. Membership-first
                          lifts it above the one-time items and gives it the
                          pencil back to the start. */}
                      {isBundleMode && bundlePricing && (
                        <div
                          className={`bg-gray-100 rounded-2xl px-5 py-3 ${
                            membershipFirst ? "order-first" : ""
                          }`}
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-900">
                              {i18n("orderSummary.recurringHeading")}
                            </h4>
                            {membershipFirst ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditProducts?.();
                                }}
                                className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
                              >
                                <img
                                  src={editIcon}
                                  alt="Edit"
                                  className="h-4 w-4"
                                />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  detachMembership();
                                }}
                                className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 transition-colors rounded-md hover:bg-gray-200"
                                aria-label={i18n("membershipUpsell.remove")}
                              >
                                <img
                                  src={binIcon}
                                  alt={i18n("membershipUpsell.remove")}
                                  className="h-3.5 w-3.5"
                                />
                              </button>
                            )}
                          </div>
                          <div className="flex items-center justify-between py-1">
                            <p className="text-sm leading-5 font-medium text-neutral-900 truncate">
                              {i18n("membershipCadence.orderLineName")}
                            </p>
                            <p className="text-sm text-neutral-900 flex-shrink-0 pl-4">
                              {formatPrice(cadenceCents, market)}/yr
                            </p>
                          </div>
                          <p className="text-xs text-neutral-500 pt-0.5">
                            {i18n("orderSummary.membershipChargedToday")}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCadenceModalOpen(true);
                            }}
                            className="mt-1 text-xs font-normal normal-case text-neutral-500 underline"
                          >
                            {i18n("paymentInfo.changeCadence")}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Location Section */}
                {selectedClinic && (
                  <div className="bg-gray-100 rounded-2xl px-5 py-3">
                    {renderClinicInfo()}
                  </div>
                )}

                {/* Time Slots Section */}
                {selectedTimeSlots && selectedTimeSlots.length > 0 && (
                  <div className="bg-gray-100 rounded-2xl px-5 py-3">
                    {renderTimeSlots()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary Bar */}
          <div className="p-4">
            {/* Header - Collapsed State */}
            <div className="mb-3">
              {/* Discount info row (if applicable). Same math as the total
                  below it: the struck figure is the pre-voucher base the
                  discount was computed on (the bundle pay-now total in bundle
                  mode), and the deduction goes through voucherDiscountCents —
                  the raw discount.value is a PERCENT for percentage codes and
                  unrounded for fixed ones, so rendering it produced "-CHF 10"
                  for a 10% code and figures the total doesn't subtract. */}
              {discount &&
                discount.value > 0 &&
                (isBundleMode ? bundlePricing : basePrice) && (
                  <div className="flex items-center justify-end gap-2 text-sm mb-1">
                    <span className="text-gray-500 line-through">
                      {formatPrice(summaryPreDiscount, market)}
                    </span>
                    <span
                      data-testid="voucher-discount-amount"
                      className="text-green-600 font-medium"
                    >
                      -{formatPrice(summaryVoucherCents, market)}
                    </span>
                  </div>
                )}

              {/* Title and Price row */}
              <div className="flex items-center justify-between">
                <button onClick={toggleExpansion} className="text-left">
                  <span className="font-medium text-gray-900">
                    {t.orderSummary}
                  </span>
                </button>
                <button
                  onClick={toggleExpansion}
                  className="flex items-center gap-2 text-right"
                >
                  <p
                    data-testid="order-summary-total"
                    className="text-lg font-bold text-gray-900"
                  >
                    {formatPrice(displayTotalPrice, market)}
                  </p>
                  <ChevronUp
                    className={`h-4 w-4 text-gray-600 transition-transform duration-300 ease-in-out ${
                      isExpanded ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              {showBackButton && onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  disabled={backDisabled}
                  className="bg-neutral-50 hover:bg-neutral-100 text-aubergine-800 rounded-full h-[44px] px-8 font-medium text-base normal-case flex items-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="order-summary-back"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t.back}
                </button>
              )}
              {currentStep === "details" ? (
                <PaymentCtaButton
                  cta={paymentCta}
                  className={`${showBackButton ? "flex-1" : "w-full"} px-8`}
                  submitTestId="order-summary-submit"
                  formId="booking-form"
                />
              ) : (
                showContinueButton && (
                  <button
                    type="button"
                    onClick={onContinue}
                    disabled={continueDisabled || !onContinue}
                    className={`${showBackButton ? "flex-1" : "w-full"} bg-aubergine-800 hover:bg-aubergine-800/90 text-white rounded-full h-[44px] px-8 font-medium text-base normal-case flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none`}
                    data-testid="order-summary-continue"
                  >
                    {t.continue}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Payment notice — hidden in the membership flow (parity with sidebar) */}
          {!membershipFlowActive && (
            <div className="px-4 pb-4">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <CheckmarkIcon />
                <span>
                  {showMemberPricing
                    ? i18n("orderSummary.yearlySubscription")
                    : i18n(paymentNoticeKey)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <CadencePickerModal
        isOpen={cadenceModalOpen}
        onClose={() => setCadenceModalOpen(false)}
        language={language}
        source="cart"
      />

      {/* Desktop sidebar is now rendered persistently in BookingCalendar */}
    </>
  );
};
